import Title from "antd/es/typography/Title";
import Text from "antd/es/typography/Text";
import Course from "../../../interfaces/course.interface";
import { object, string } from "yup";
import moment from "moment";
import {
  createCourse,
  updateCourseById,
} from "../../../services/course.service";
import ScheduleVenues from "../../../interfaces/schedule-venues.interface";
import { Costs } from "../../../interfaces/course-cost.interface";
import GSTLicenses from "../../../interfaces/gst-licenses";
import { UploadFile } from "antd";
import User from "../../../interfaces/user.interface";
import { NotificationBuilder } from "../../../interfaces/survey.interface";

type CardProps = {
  children: string | React.ReactNode | React.ReactNode[];
  title: string;
};

type Yup = {
  code: string;
  name: string;
  year: string;
  term: string;
  status: string;
  hidingStatus: string;
  starts: string;
  ends: string;
  withdrawByDate: string;
  midTermStarts: string;
  midTermEnds: string;
};

export const Card = ({ title, children }: CardProps) => {
  return (
    <div className="rounded overflow-hidden shadow-lg w-full">
      <div className="bg-gray-200 px-4 pt-1 pb-2">
        <Title style={{ fontWeight: "500" }} level={3}>
          {title}
        </Title>
      </div>
      {children}
    </div>
  );
};

export const courseInfo = (
  prop: keyof Course,
  title: string,
  course: Course,
) => {
  return (
    <div className="flex flex-col items-baseline px-4">
      <Title level={3} style={{ fontWeight: "400" }}>
        {title}:
      </Title>
      <div
        className="custom-format text-base"
        style={{ fontWeight: "300" }}
        dangerouslySetInnerHTML={{ __html: String(course[prop] ?? "") }}
      />
    </div>
  );
};

export const gradeOption = (
  label: string,
  key: keyof Course,
  course: Course,
) => {
  return (
    <div className="flex items-baseline justify-between">
      <Title level={5} className="font-semibold" style={{ lineHeight: "0" }}>
        {label}:
      </Title>
      <Text className="font-semibold">{String(course[key])}</Text>
    </div>
  );
};

export const dateItem = (label: string, key: keyof Course, course: Course) => {
  return (
    <div className="flex items-baseline justify-between gap-3">
      <Title level={5} className="" style={{ lineHeight: "0" }}>
        {label}:
      </Title>
      <Text className="float-right text-left break-keep">
        {String(course[key])}
      </Text>
    </div>
  );
};

export const courseDetailSchema = object({
  "code": string().required(),
  "name": string().required(),
  "year": string().required(),
  "term": string().required(),
  "purpose": string().required(),
  "selectedTopics": string().required(),
  "approach": string().required(),
  "preRequisite": string().required(),
  "status": string().required(),
  "hidingStatus": string().required(),
  "starts": string().required(),
  "ends": string().required(),
  "withdrawByDate": string().required(),
  "midTermStarts": string().required(),
  "midTermEnds": string().required(),
  "Audit": string().required(),
  "Webcast": string().required(),
  "In Class": string().required(),
  "Webcast: Audit": string().required(),
});

export const courseDetailValues = (course?: Course) => {
  return {
    "code": course?.code ?? "",
    "name": course?.name ?? "",
    "year": course?.year ?? "",
    "term": course?.term ?? "",
    "purpose": course?.purpose ?? "",
    "selectedTopics": course ? course["selectedTopics"] : "",
    "approach": course ? course["approach"] : "",
    "preRequisite": course ? course["preRequisite"] : "",
    "status": course ? course["status"] : "Registration Closed",
    "hidingStatus": course ? course["hidingStatus"] : "2",
    "starts": course?.starts ?? moment().format("MM/DD/YYYY"),
    "ends": course?.ends ?? moment().format("MM/DD/YYYY"),
    "withdrawByDate": course?.withdrawByDate ?? moment().format("MM/DD/YYYY"),
    "midTermStarts": course?.midTermStarts ?? moment().format("MM/DD/YYYY"),
    "midTermEnds": course?.midTermEnds ?? moment().format("MM/DD/YYYY"),
    "Audit":
      course && course.costs?.length
        ? (course.costs.find((c) => c.registrationType === "Audit")?.leyend ??
          "")
        : "",
    "Webcast":
      course && course.costs?.length
        ? (course.costs.find((c) => c.registrationType === "Webcast")?.leyend ??
          "")
        : "",
    "In Class":
      course && course.costs?.length
        ? (course.costs.find((c) => c.registrationType === "In Class")
            ?.leyend ?? "")
        : "",
    "Webcast: Audit":
      course && course.costs?.length
        ? (course.costs.find((c) => c.registrationType === "Webcast: Audit")
            ?.leyend ?? "")
        : "",
  };
};

export const manageCourseUpdate = async (
  id: string,
  course: Course,
  yup: any,
  scheduleVenues: ScheduleVenues[],
  costs: Costs[],
  gstLicenses: GSTLicenses[],
  fileList: UploadFile[],
  deletedFiles: number[],
  user: User,
) => {
  console.log("RESPSP: ", gstLicenses);
  const obj: Course = {
    id: Number(id),
    assignments: course.assignments,
    projects: course.projects,
    special: course.special,
    midTerm: course.midTerm,
    finalExam: course.finalExam,
    ...yup,
    scheduleVenues: scheduleVenues.map((s: ScheduleVenues) => ({
      ...s,
      key: undefined,
    })),
    costs: costs.map((s: Costs) => ({
      ...s,
      key: undefined,
      leyend: yup[s.registrationType],
      active: s.active === "Yes" || s.active === "1" ? "1" : "0",
      enableWaitlist:
        s.enableWaitlist === "Yes" || s.enableWaitlist === "1" ? "1" : "0",
    })),
    gstLicenses: gstLicenses,
    filesList: fileList,
    deletedFiles,
    institutionId: user?.institutionId!,
    type: course.type,
  };
  if (id.includes("New")) {
    const res_id = await createCourse(obj);
    id = String(res_id.data.course.id);
  } else {
    await updateCourseById(String(id), obj, fileList);
  }

  return { success: true, data: { course: { id: id } } };
};

export const createObjCourse = (id: string) => {
  return {
    id: 0,
    year: "",
    term: "",
    code: "",
    name: id.includes("Course") ? "New Course" : "New Workshop",
    status: "Registration Closed",
    hidingStatus: "2",
    purpose: "",
    preRequisite: "",
    selectedTopics: "",
    starts: "",
    ends: "",
    midTermStarts: "",
    midTermEnds: "",
    approach: "",
    assignments: 0,
    projects: 0,
    special: 0,
    midTerm: 0,
    finalExam: 0,
    finalConfirmation: "no",
    type: id.includes("Course") ? "Course" : "Workshop",
    costs: [
      {
        registrationType: "Audit",
        enableType: "No",
        baseCost: "",
        earlyDueDate: moment().format("MM/DD/YYYY"),
        discountBeforeDueDate: "",
        discountMember: "",
        paymentMethods: "Both",
        maxOfStudents: "",
        enableWaitlist: "No",
        active: "",
        leyend: "",
        hleyend: "",
        numerando: "",
        productCurrency: "",
      },
      {
        registrationType: "Webcast",
        enableType: "No",
        baseCost: "",
        earlyDueDate: moment().format("MM/DD/YYYY"),
        discountBeforeDueDate: "",
        discountMember: "",
        paymentMethods: "Both",
        maxOfStudents: "",
        enableWaitlist: "No",
        active: "",
        leyend: "",
        hleyend: "",
        numerando: "",
        productCurrency: "",
      },
      {
        registrationType: "In Class",
        enableType: "No",
        baseCost: "",
        earlyDueDate: moment().format("MM/DD/YYYY"),
        discountBeforeDueDate: "",
        discountMember: "",
        paymentMethods: "Both",
        maxOfStudents: "",
        enableWaitlist: "No",
        active: "",
        leyend: "",
        hleyend: "",
        numerando: "",
        productCurrency: "",
      },
      {
        registrationType: "Webcast: Audit",
        enableType: "No",
        baseCost: "",
        earlyDueDate: moment().format("MM/DD/YYYY"),
        discountBeforeDueDate: "",
        discountMember: "",
        paymentMethods: "Both",
        maxOfStudents: "",
        enableWaitlist: "No",
        active: "",
        leyend: "",
        hleyend: "",
        numerando: "",
        productCurrency: "",
      },
    ],
    scheduleVenues: [],
    gstLicenses: [],
    files: [],
  };
};

export const checkYupProperties = (yup: Yup): boolean => {
  const properties: (keyof Yup)[] = [
    "code",
    "name",
    "year",
    "term",
    "status",
    "hidingStatus",
  ];

  for (let i = 0; i < properties.length; i++) {
    const property = properties[i];
    if (!yup[property] || yup[property].trim() === "") return false;
  }
  return true;
};

const DISABLE_REGISTRATION_ERROR =
  "You cannot disable this registration type because there are students registered to it. To disable it, move those students to a different registration type first.";

export const checkCourseProperties = (
  costs: Costs[] | undefined,
  originalCosts?: Costs[],
): string => {
  if (!costs?.length) {
    return "Missing cost information";
  }
  if (originalCosts?.length) {
    for (const cost of costs) {
      const isBeingDisabled = cost.active === "No" || cost.active === "0";
      if (!isBeingDisabled) continue;
      const original = originalCosts.find(
        (c) => c.id === cost.id || c.registrationType === cost.registrationType,
      );
      if (!original) continue;
      const wasActive = original.active === "Yes" || original.active === "1";
      const registeredCount = Number(original.registered ?? 0);
      if (wasActive && registeredCount > 0) {
        return `${original.registrationType}: ${DISABLE_REGISTRATION_ERROR}`;
      }
    }
  }
  for (let cost of costs) {
    if (cost.active === "Yes" || cost.active === "1") {
      if (
        cost.baseCost &&
        cost.discountBeforeDueDate &&
        cost.discountMember &&
        cost.maxOfStudents
      ) {
        return "";
      }
      return `${cost.registrationType} cost has missing information`;
    }
  }
  return "Configure at least one cost option";
};

export const notCompleteObj = (
  course: string,
  type: boolean,
  message?: string,
): NotificationBuilder => {
  let content;
  if (type)
    content = `The ${course} has some missing required fields, please complete all the fields mark as a required.`;
  else
    content = `The ${course} has NOT include any valid kind of Registration Type.`;
  if (message) content = message;
  return {
    type: "error",
    content: content,
    duration: 5,
    style: { marginTop: "6vh" },
  };
};
